/**
 * Schedule operations — startAtIndex table management.
 */

import type { Transaction } from "dexie"
import { StartAtType, StartAtSubType, ObjectType } from "./db-types.js"
import type { DbStartAtIndex } from "./db-types.js"
import { getDb, callUndo, getNow, emitEvent } from "./runtime.js"
import { ok, fail } from "./op-types.js"
import type { OpResult } from "./op-types.js"
import { getInsertIndex } from "./helpers/indexHelpers.js"
import { cascadeDescendants } from "./helpers/cascadeHelpers.js"
import { serializeTaskMoves } from "./helpers/queueHelpers.js"
import { startAtTypeLabel, toMsTimestamp } from "./utils.js"

const INBOX_PATH = "root/#inbox"
const ANYDAY_PATH = "root/#anyday"

/**
 * Move a scheduled inbox task to Anyday and take its checklist along (every descendant lives
 * under `${parentPath}/${id}`), as moveTask does. The descendants go first: dexie-sqlite does
 * not roll back a failed transaction, and a task whose rows stayed behind would show an empty
 * checklist. Inbox and Anyday are both virtual, so the realm does not change.
 */
async function moveInboxTaskToAnyday(
  id: string,
  index: number,
  timestamp: string,
  tx: Transaction,
): Promise<void> {
  await cascadeDescendants(id, INBOX_PATH, ANYDAY_PATH, undefined, tx)
  await getDb().totodo.update(id, {
    updatedAt: timestamp,
    parentPath: ANYDAY_PATH,
    index,
  })
}

// ---------------------------------------------------------------------------
// scheduleTask
// ---------------------------------------------------------------------------

/** scheduleTask's reads and writes for a date: the startAtIndex entry, and Anyday for an inbox task. */
async function writeSchedule(
  id: string,
  startAt: string,
  type: StartAtType,
  subType: StartAtSubType,
  timestamp: string,
): Promise<OpResult> {
  const db = getDb()
  const entryId = `#${id}`
  const [existing, task] = await Promise.all([
    db.startAtIndex.get(entryId),
    db.totodo.get(id),
  ])

  if (!task) return fail(`scheduleTask: task not found: ${id}`)

  if (existing) {
    const updates: Partial<DbStartAtIndex> = {}
    if (existing.startAt !== startAt) updates.startAt = startAt
    if (existing.type !== type) updates.type = type
    if (existing.subType !== subType) updates.subType = subType
    if (Object.keys(updates).length > 0) {
      await db.startAtIndex.update(entryId, updates)
    }

    const isInbox = task.parentPath === INBOX_PATH
    if (isInbox) {
      const newIndex = await getInsertIndex(
        ANYDAY_PATH,
        "bottom",
        task.type as ObjectType,
      )
      await db.transaction("rw", [db.totodo], (tx) =>
        moveInboxTaskToAnyday(id, newIndex, timestamp, tx),
      )
    } else {
      await db.totodo.update(id, { updatedAt: timestamp })
    }
  } else {
    const lastEntry = await db.startAtIndex.orderBy("index").last()
    const index = lastEntry ? lastEntry.index + 1 : 0

    const isInbox = task.parentPath === INBOX_PATH
    const newIndex = isInbox
      ? await getInsertIndex(ANYDAY_PATH, "bottom", task.type as ObjectType)
      : undefined

    await db.transaction("rw", [db.totodo, db.startAtIndex], async (tx) => {
      await db.startAtIndex.add({
        id: entryId,
        index,
        startAt,
        type,
        subType,
      })
      if (newIndex !== undefined) {
        await moveInboxTaskToAnyday(id, newIndex, timestamp, tx)
      } else {
        await db.totodo.update(id, { updatedAt: timestamp })
      }
    })
  }
  return ok()
}

export async function scheduleTask(
  id: string,
  startAt: string | undefined,
  type: StartAtType = StartAtType.Default,
  subType: StartAtSubType = StartAtSubType.Default,
): Promise<OpResult> {
  const db = getDb()
  try {
    const entryId = `#${id}`
    const timestamp = getNow()
    // Normalize to ms-timestamp string (accepts ISO, ms number, or ms string)
    startAt = startAt != null ? toMsTimestamp(startAt) ?? startAt : undefined

    if (startAt === undefined) {
      // An id with no object behind it is not "cleared": say so (the call used to answer ok).
      if (!(await db.totodo.get(id))) return fail(`scheduleTask: task not found: ${id}`)
      const existing = await db.startAtIndex.get(entryId)
      if (existing) {
        const prevStartAt = existing.startAt
        const prevType = existing.type
        const prevSubType = existing.subType
        await db.startAtIndex.delete(entryId)
        await db.totodo.update(id, { updatedAt: timestamp })

        callUndo(
          "Cleared schedule",
          async () => {
            await scheduleTask(
              id,
              prevStartAt,
              prevType as StartAtType,
              prevSubType as StartAtSubType,
            )
          },
          async () => {
            await scheduleTask(id, undefined)
          },
        )
      } else {
        await db.totodo.update(id, { updatedAt: timestamp })
      }
      return ok()
    }

    // Reads whether the task is in the inbox and moves it to Anyday after any other move of it
    // (see serializeTaskMoves): a move from a stale read would leave the checklist behind.
    const written = await serializeTaskMoves([id], () =>
      writeSchedule(id, startAt!, type, subType, timestamp),
    )
    if (!written.ok) return written

    callUndo(
      "Scheduled task",
      async () => {
        const db = getDb()
        const entry = await db.startAtIndex.get(entryId)
        if (entry) {
          await db.startAtIndex.delete(entryId)
        }
      },
      async () => {
        await scheduleTask(id, startAt, type, subType)
      },
    )

    emitEvent("task_scheduled", { id, startAtType: startAtTypeLabel(type) })
    return ok()
  } catch (e) {
    return fail(
      `scheduleTask failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// clearSchedule
// ---------------------------------------------------------------------------

export async function clearSchedule(id: string): Promise<OpResult> {
  return scheduleTask(id, undefined)
}

// ---------------------------------------------------------------------------
// unscheduleContainer
// ---------------------------------------------------------------------------

export async function unscheduleContainer(id: string): Promise<OpResult> {
  const db = getDb()
  try {
    const entryId = `#${id}`
    const existing = await db.startAtIndex.get(entryId)

    if (!existing) return ok()

    const prevStartAt = existing.startAt
    const prevType = existing.type
    const prevSubType = existing.subType
    const timestamp = getNow()

    await db.transaction("rw", [db.totodo, db.startAtIndex], async () => {
      await db.startAtIndex.delete(entryId)
      await db.totodo.update(id, { updatedAt: timestamp })
    })

    callUndo(
      "Activated project",
      async () => {
        await scheduleTask(
          id,
          prevStartAt,
          prevType as StartAtType,
          prevSubType as StartAtSubType,
        )
      },
      async () => {
        await unscheduleContainer(id)
      },
    )

    return ok()
  } catch (e) {
    return fail(
      `unscheduleContainer failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// activateAndPositionContainer
// ---------------------------------------------------------------------------

export async function activateAndPositionContainer(
  id: string,
  newIndex: number,
): Promise<OpResult> {
  const db = getDb()
  try {
    const entryId = `#${id}`
    const existing = await db.startAtIndex.get(entryId)
    const prevPrivateIndex = await db.privateIndex.get(entryId)

    const prevStartAt = existing?.startAt
    const prevType = existing?.type
    const prevSubType = existing?.subType
    const prevSortIndex = prevPrivateIndex?.index
    const timestamp = getNow()

    await db.transaction(
      "rw",
      [db.totodo, db.startAtIndex, db.privateIndex],
      async () => {
        if (existing) {
          await db.startAtIndex.delete(entryId)
        }
        await db.privateIndex.put({ id: entryId, index: newIndex })
        await db.totodo.update(id, { updatedAt: timestamp })
      },
    )

    callUndo(
      "Activated project",
      async () => {
        const db = getDb()
        if (prevStartAt != null && prevType != null) {
          await scheduleTask(
            id,
            prevStartAt,
            prevType as StartAtType,
            prevSubType as StartAtSubType,
          )
        }
        if (prevSortIndex != null) {
          await db.privateIndex.put({ id: entryId, index: prevSortIndex })
        } else {
          await db.privateIndex.delete(entryId)
        }
        await db.totodo.update(id, { updatedAt: getNow() })
      },
      async () => {
        await activateAndPositionContainer(id, newIndex)
      },
    )

    return ok()
  } catch (e) {
    return fail(
      `activateAndPositionContainer failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}
